import React from 'react'
import { IconChevronRight } from '@tabler/icons-react'
import classNames from 'classnames'
import { kebabCase } from 'lodash'

import './Accordion.scss'

/**
 * @import import { Accordion } from '@/components/accordions/Accordion'
 * @param {object} props
 * @param {boolean} [props.expanded=true] Whether the content section is visible
 * @param {(id: string) => void} props.handleToggle Called with the accordion id when the header is clicked or activated with Enter or Space
 * @param {React.ReactNode} props.title Heading text in the header
 * @param {string} [props.id] Identifier passed to handleToggle; defaults to the kebab-cased title
 * @param {React.ReactNode} [props.children] Content revealed while expanded
 * @param {React.ReactNode} [props.actions] Elements rendered at the right edge of the header
 * @param {boolean} [props.large] Larger heading text
 * @param {boolean} [props.styled] Border, border-radius, and header background
 * @param {React.ReactNode} [props.icon] Icon rendered before the chevron
 */
export const Accordion = ({
  expanded = true,
  handleToggle,
  title,
  id,
  children,
  actions,
  large,
  styled,
  icon,
  className,
  ...props
}) => {
  const defaultId = id || kebabCase(title)

  return (
    <div
      className={classNames('accordion', className, { styled })}
      data-testid={`accordion-${defaultId}`}
      {...props}
    >
      <header
        role="button"
        tabIndex={0}
        data-testid={`accordion-button-${defaultId}`}
        onClick={() => handleToggle(defaultId)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            handleToggle(defaultId)
          }
        }}
      >
        <div className="flex-align-center medium-gap">
          {icon}
          <IconChevronRight className={classNames('icon-svg accordion-icon', { expanded })} />
          <h3 className={classNames({ large })}>{title}</h3>
        </div>
        <div className="flex-align-center small-gap">{actions}</div>
      </header>
      {expanded && <section data-testid={`content-${defaultId}`}>{children}</section>}
    </div>
  )
}
